/**
 * React Query hooks for secret operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useSecrets
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { secretService } from '@/services';
import { sandboxGroupService } from '@/services';

/**
 * Query key factory for secrets.
 * Ensures consistent cache key generation.
 */
export const secretKeys = {
  all: ['secrets'] as const,
  lists: () => [...secretKeys.all, 'list'] as const,
  list: () => [...secretKeys.lists()] as const,
  keys: (secretId: string, groupId?: string) =>
    [...secretKeys.all, 'keys', secretId, groupId ?? 'user'] as const,
};

/**
 * Hook to fetch all secrets (metadata only)
 *
 * @example
 * const { data, isLoading, error } = useSecrets();
 */
export function useSecrets(enabled = true) {
  return useQuery({
    queryKey: secretKeys.list(),
    queryFn: () => secretService.list(),
    enabled,
  });
}

/**
 * Hook to upsert a secret
 *
 * @example
 * const upsertMutation = useUpsertSecret();
 * upsertMutation.mutate({ secretId: 'my-secret', values: { key: 'value' } });
 */
export function useUpsertSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ secretId, values }: { secretId: string; values: Record<string, string> }) =>
      secretService.upsert(secretId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: secretKeys.all });
    },
  });
}

/**
 * Hook to delete a secret
 *
 * @example
 * const deleteMutation = useDeleteSecret();
 * deleteMutation.mutate('my-secret');
 */
export function useDeleteSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secretId: string) => secretService.delete(secretId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: secretKeys.all });
    },
  });
}

/**
 * Hook to fetch key names for a specific secret.
 * Uses the dedicated keys endpoint — secret values are never transferred.
 *
 * @param secretId - The secret ID to fetch keys for
 * @param groupId - Optional sandbox group ID (uses group-scoped endpoint when provided)
 *
 * @example
 * const { data: keys } = useSecretKeys('my-api-key');
 * // keys = ['token', 'api-key']
 */
export function useSecretKeys(secretId: string, groupId?: string) {
  return useQuery({
    queryKey: secretKeys.keys(secretId, groupId),
    queryFn: () =>
      groupId
        ? sandboxGroupService.listSecretKeys(groupId, secretId)
        : secretService.listKeys(secretId),
    enabled: !!secretId,
    staleTime: 30_000,
  });
}
